/**
 * Provides the `processJSON` utility function for processing JSON data, and its associated `ProcessJsonData` and `ProcessJsonInputData` types.
 * @module processJSON
 */
// Import global utils
import { isEmpty } from '@automator/shared/utils/is-empty';
/**
 * Processes a JSON string representing an array of objects to extract all unique keys present across the objects and count the total number of items (objects) in the array.
 *
 * **Behavior:**
 * - Parses the input `jsonText` string using `JSON.parse()`.
 * - Validates that the parsed data is an array. Throws an error if not.
 * - Iterates through each object in the array.
 * - Collects all unique keys from these objects.
 * - Counts the total number of objects in the array.
 * - Returns an object `{ type: 'json', keys: string[], numberOfItems: number }`.
 *
 * **Error handling:**
 * - Throws an error if `jsonText` is null, undefined, empty, or contains only whitespace.
 * - Throws an error if `JSON.parse()` fails (e.g., invalid JSON syntax).
 * - Throws an error if the parsed JSON is not an array.
 * - Handles empty arrays correctly (returns empty `keys` and `numberOfItems: 0`).
 *
 * @since 5.10 Initial implementation.
 * @param {string | null | undefined} jsonText - The JSON data as a string, expected to represent an array of objects. Can be null or undefined.
 * @return {ProcessJsonData} An object containing the unique `keys` found and the `numberOfItems` count.
 * @throws {SyntaxError} If `jsonText` is not valid JSON after trimming.
 * @throws {Error} If the parsed JSON data is not an array, or if the input `jsonText` is null, undefined, empty, or whitespace-only.
 *
 * @example Importing the processJSON utility
 * ```typescript
 * import { processJSON } from '@automator/shared/utils/get-file-data/process-json';
 * ```
 *
 * @example Process JSON coming from the server
 * If you want to process a JSON coming from the server, use the `getFileData` utility instead of `processJSON`.
 * ```typescript
 * const jsonData = await getFileData( 'json', 'https://example.com/data.json' );
 * ```
 *
 * @example Processing valid JSON array
 * ```typescript
 * const jsonString = '[ { "name": "Alice", "age": 30 }, { "name": "Bob", "city": "London", "age": 25 } ]';
 * processJSON( jsonString );
 * // Returns: { type: 'json', keys: [ 'name', 'age', 'city' ], numberOfItems: 2 }
 * // Note: Key order might vary.
 * ```
 *
 * @example Processing empty JSON array
 * ```typescript
 * const emptyJsonArray = '[]';
 * processJSON( emptyJsonArray );
 * // Returns: { type: 'json', keys: [], numberOfItems: 0 }
 * ```
 *
 * @example Handling invalid JSON input
 * ```typescript
 * const invalidJson = '[ { "name": "Alice" }'; // Missing closing bracket
 *
 * try {
 *     processJSON( invalidJson );
 * } catch ( error ) {
 *     console.error( error ); // Catches SyntaxError from JSON.parse
 * }
 * ```
 *
 * @example Handling non-array JSON input
 * ```typescript
 * const jsonObject = '{ "key": "value" }'; // Not an array
 * try {
 *     processJSON( jsonObject );
 * } catch ( error ) {
 *     console.error( error ); // Catches Error: JSON data is not an array
 * }
 * ```
 *
 * @example Handling empty/null/whitespace input
 * ```typescript
 * try {
 *     processJSON( null ); // Or '', undefined, '  \n  '
 * } catch ( error ) {
 *     console.error( error ); // Catches Error: Input JSON text cannot be null, undefined, or empty.
 * }
 * ```
 */
export function processJSON(jsonText) {
    // Check for null, undefined, empty, or whitespace-only strings
    if (isEmpty(jsonText) || (typeof jsonText === 'string' && jsonText.trim() === '')) {
        // Throw an error if input is invalid for the use case
        throw new Error('Input JSON text cannot be null, undefined, or empty.');
    }
    let jsonData;
    // Attempt to parse the JSON string
    try {
        jsonData = JSON.parse(jsonText);
    }
    catch (error) {
        // Re-throw JSON parsing errors for clarity
        console.error('Failed to parse JSON:', error);
        throw new SyntaxError(`Invalid JSON input: ${error.message}`);
    }
    // Validate that the parsed data is an array
    if (!Array.isArray(jsonData)) {
        throw new Error('JSON data is not an array.');
    }
    // This type assertion is safe since we've validated it's an array
    const jsonArray = jsonData;
    // Use a Set to collect unique keys
    const keys = jsonArray.reduce((acc, item) => {
        // Ensure item is actually an object before getting keys
        if (item && typeof item === 'object' && !Array.isArray(item)) {
            Object.keys(item).forEach(key => acc.add(key));
        }
        // If item is not an object (e.g., array contains null or primitives), ignore it for key extraction.
        return acc;
    }, new Set());
    const numberOfItems = jsonArray.length;
    return {
        type: 'json',
        keys: Array.from(keys), // Convert Set back to Array
        numberOfItems: numberOfItems,
    };
}
;
